import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const jumpingInternationalMonaco: ChauffeurPage = {
  slug: "jumping-international-monaco",
  title: "Chauffeur privé Jumping International de Monaco – VTC VIP",
  subtitle:
    "Chauffeur privé pendant le Jumping International de Monaco : accès Port Hercule, accueil aéroport de Nice et mises à disposition autour de l'épreuve de saut d'obstacles.",
  keywords: [
    "chauffeur jumping monaco",
    "vtc jumping international monaco",
    "chauffeur saut d'obstacles monaco",
  ],
  hero: {
    title: "Chauffeur privé pendant le Jumping International de Monaco",
    subtitle:
      "Longines Global Champions Tour sur les quais de Port Hercule : un chauffeur dédié quand Monaco se ferme et que le stationnement disparaît.",
    keywords: [
      "chauffeur Jumping Monaco",
      "VTC Port Hercule",
      "chauffeur Global Champions Tour",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le Jumping International de Monaco",
    badges: [
      "Accueil aéroport VIP",
      "Dépose Port Hercule",
      "Mise à disposition",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le Jumping",
    items: [
      {
        title: "Port Hercule sans stationnement",
        description:
          "Les quais accueillent l'arène temporaire : voirie réduite et parkings saturés. Le chauffeur vous dépose au plus près et repart, sans tourner en rond.",
      },
      {
        title: "Accueil aéroport de Nice",
        description:
          "L'aéroport de Nice Côte d'Azur est à une trentaine de minutes. Accueil pancarte, suivi de vol et transfert direct vers votre hôtel monégasque.",
      },
      {
        title: "Rythme de l'épreuve maîtrisé",
        description:
          "Épreuves en journée et en soirée fin juin : le chauffeur cale ses prises en charge sur le programme du saut d'obstacles.",
      },
      {
        title: "Hospitalité et soirées",
        description:
          "Paddock, dîners et hôtels du Carré d'Or : retours de nuit avec un chauffeur qui patiente.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le Jumping de Monaco",
    services: [
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert Nice ↔ Monaco avec suivi de vol pendant le week-end du Jumping.",
      },
      {
        title: "Dépose Port Hercule",
        text: "Approche au plus près des quais selon les fermetures autour de l'arène de saut d'obstacles.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié à l'heure ou à la journée pour enchaîner épreuves, hospitalité et rendez-vous.",
      },
      {
        title: "Soirées & dîners",
        text: "Restaurants du Carré d'Or, Monte-Carlo Bay et palaces monégasques, retour de nuit assuré.",
      },
      {
        title: "Navettes invités & partenaires",
        text: "Coordination multi-véhicules pour délégations, sponsors et invités VIP.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le Jumping",
    items: [
      {
        name: "Port Hercule",
        description:
          "Quais accueillant l'arène temporaire du saut d'obstacles.",
      },
      {
        name: "Carré d'Or & Casino",
        description: "Hôtel de Paris, Hermitage et Place du Casino.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, à une trentaine de minutes de Monaco.",
      },
      {
        name: "Fairmont & Monte-Carlo Bay",
        description: "Hôtels et hospitalités en bord de mer.",
      },
      {
        name: "Beausoleil & abords",
        description: "Résidences et accès depuis les hauteurs de Monaco.",
      },
    ],
  },
  generic: {
    heading: "Le Jumping International de Monaco vu d'un chauffeur",
    subheading:
      "Fin juin, l'arène de saut d'obstacles s'installe sur les quais de Port Hercule et le centre de Monaco se resserre.",
    paragraphs: [
      "Chaque fin juin, le Jumping International de Monaco dresse une arène temporaire sur les quais de Port Hercule pour une étape du Longines Global Champions Tour. La voirie du port est réduite et le stationnement, déjà rare à Monaco, devient introuvable : un chauffeur qui connaît les déposes autorisées vous rapproche de l'entrée sans tourner.",
      "La majorité des arrivées passe par l'aéroport de Nice Côte d'Azur, à une trentaine de minutes. Avec le suivi de vol et l'accueil pancarte, nous absorbons les décalages pour vous conduire directement à votre hôtel du Carré d'Or ou du bord de mer.",
      "Les épreuves s'étalent sur le week-end, en journée comme en soirée, entre paddock, hospitalité et dîners. La mise à disposition garde le même chauffeur sur toute la journée et assure les retours de nuit.",
    ],
    bullets: [
      "Dépose au plus près de Port Hercule",
      "Suivi de vol fin juin",
      "Retours de nuit après les épreuves",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le Jumping de Monaco",
    steps: [
      "Indiquez vos dates du week-end, votre hôtel et le programme des épreuves qui vous intéressent.",
      "Nous calons les itinéraires selon les fermetures autour de Port Hercule et l'affluence de Monaco.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Transfert direct ou mise à disposition pour couvrir épreuves, hospitalité et soirées.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur privé pour le Jumping International de Monaco ?",
      a: "Réservez tôt : fin juin, Monaco cumule le Jumping et une forte affluence estivale. Précisez vos dates, votre hôtel et le programme des épreuves pour que nous bloquions un chauffeur dédié sur tout le week-end.",
    },
    {
      q: "Pouvez-vous déposer au plus près de Port Hercule ?",
      a: "Oui. Pendant l'épreuve, les quais accueillent l'arène de saut d'obstacles et la circulation est réduite. Le chauffeur vous dépose au point autorisé le plus proche de l'entrée, puis vous reprend à la sortie.",
    },
    {
      q: "Gérez-vous l'arrivée depuis l'aéroport de Nice ?",
      a: "Oui, avec suivi de vol et accueil pancarte. L'aéroport de Nice Côte d'Azur est à une trentaine de minutes ; nous adaptons l'heure de prise en charge en cas de retard et vous déposons directement à Monaco.",
    },
    {
      q: "Proposez-vous une mise à disposition sur la journée ?",
      a: "Oui. Un chauffeur dédié reste à votre disposition à l'heure ou à la journée pour enchaîner épreuves, hospitalité et rendez-vous sans vous soucier du stationnement monégasque.",
    },
    {
      q: "Assurez-vous les retours après les soirées ?",
      a: "Oui. Dîners du Carré d'Or et hospitalités se terminent tard : le chauffeur patiente et garantit votre retour de nuit vers votre hôtel ou votre résidence.",
    },
    {
      q: "Pourquoi choisir KS Rent Services pour le Jumping ?",
      a: "Notre service de chauffeur privé sur la Côte d'Azur est noté 5,0/5 sur 38 avis Google : chauffeur en tenue, ponctuel et discret, habitué aux contraintes d'accès et de stationnement de Monaco.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur privé à Monaco", href: "/chauffeur/monaco" },
    {
      label: "Réserver un chauffeur à Nice",
      href: "/chauffeur/nice",
    },
    {
      label: "Transferts depuis l’aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    {
      label: "Voitures à louer à Monaco",
      href: "/location/location-voiture-monaco",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le Jumping de Monaco",
    text: "Accès Port Hercule, accueil aéroport et soirées : réservez un chauffeur dédié pour le week-end du Jumping.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur Jumping International de Monaco — VTC VIP",
    description:
      "Chauffeur privé pendant le Jumping International de Monaco : accès Port Hercule, accueil aéroport de Nice, mise à disposition et retours de nuit. Réservez.",
    canonical: "/chauffeur/jumping-international-monaco",
  },
};

export default jumpingInternationalMonaco;
